import { NavLink, useLocation, useNavigate, useMatch } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser } from '@fortawesome/pro-regular-svg-icons/faUser';
import { faGlobe } from '@fortawesome/pro-regular-svg-icons/faGlobe';
import { faCalendarStar } from '@fortawesome/pro-regular-svg-icons/faCalendarStar';
import { faCommentDots } from '@fortawesome/pro-regular-svg-icons/faCommentDots';
import { type IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { Capacitor } from '@capacitor/core';
import { twMerge } from 'tailwind-merge';
import { useTranslation } from 'react-i18next';
import { useCallback, useEffect, useMemo, useRef } from 'react';

import { useCurrentUser } from 'FrontRoyalAngular';
import { useUnreadMessageCount } from 'Messaging';
import mobileFadeSrc from 'images/network-mobile-fade.png';
import { Card } from 'Card';
import '../locales/studentNetworkV2/studentNetworkV2-en.json';

type NavSection = {
    labelKey: string;
    path: string;
    icon: IconDefinition;
};

const allSections: NavSection[] = [
    { labelKey: 'map', path: '/student-network/map', icon: faGlobe },
    { labelKey: 'people', path: '/student-network/people', icon: faUser },
    { labelKey: 'events', path: '/student-network/events', icon: faCalendarStar },
    { labelKey: 'messages', path: '/student-network/messages', icon: faCommentDots },
];

function useSections() {
    const currentUser = useCurrentUser();
    const showMessages = currentUser?.messagingEnabled && currentUser.sendbirdAccessToken;
    return useMemo(
        () => (showMessages ? allSections : allSections.filter(s => s.labelKey !== 'messages')),
        [showMessages],
    );
}

function UnreadBadge({ className }: { className?: string }) {
    const count = useUnreadMessageCount();
    if (count <= 0) return null;
    const label = count > 99 ? '99+' : String(count);
    return (
        <span
            className={twMerge(
                'flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-blue px-1 text-[10px] font-semibold leading-none text-white',
                className,
            )}
        >
            {label}
        </span>
    );
}

function DesktopNavItem({ section, label, badge }: { section: NavSection; label: string; badge?: boolean }) {
    return (
        <NavLink
            to={section.path}
            className={({ isActive }) =>
                twMerge(
                    'relative flex w-fit items-center gap-3.5 rounded-full px-5 py-3.5 text-lg font-normal text-black no-underline transition-colors',
                    isActive ? 'bg-slate-grey-100-alt' : 'hover:bg-slate-grey-50',
                )
            }
        >
            <FontAwesomeIcon icon={section.icon} className="w-6 text-center text-lg" fixedWidth />
            {label}
            {badge && (
                <UnreadBadge className="absolute -end-0.5 -top-0.5 h-[22px] min-w-[22px] border-2 border-white text-xs" />
            )}
        </NavLink>
    );
}

// Minimum finger movement (px) before a touch is treated as a drag rather than a tap.
const DRAG_THRESHOLD_PX = 8;

// On native Capacitor (WKWebView), after a route transition from another part of the app,
// the synthesized `click` event for the nav item adjacent to the active one can use stale
// touch coordinates, causing it to hit the active item (a no-op) instead. Bypassing click
// synthesis by navigating directly on touchend avoids this WKWebView quirk entirely.
function MobileNavItem({ section, label, badge }: { section: NavSection; label: string; badge?: boolean }) {
    const isNative = Capacitor.isNativePlatform();
    const navigate = useNavigate();
    const isActive = !!useMatch(section.path);
    const touchStartRef = useRef<{ x: number; y: number } | null>(null);

    const onTouchStart = useCallback((e: React.TouchEvent) => {
        touchStartRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
    }, []);

    const onTouchEnd = useCallback(
        (e: React.TouchEvent) => {
            if (!touchStartRef.current || !e.changedTouches[0]) return;
            const dx = Math.abs(e.changedTouches[0].clientX - touchStartRef.current.x);
            const dy = Math.abs(e.changedTouches[0].clientY - touchStartRef.current.y);
            touchStartRef.current = null;
            // Only navigate if the touch was a tap (minimal movement), not a drag
            if (dx < DRAG_THRESHOLD_PX && dy < DRAG_THRESHOLD_PX) {
                e.preventDefault();
                navigate(section.path);
            }
        },
        [navigate, section.path],
    );

    if (isNative) {
        return (
            <div
                role="link"
                onTouchStart={onTouchStart}
                onTouchEnd={onTouchEnd}
                className={twMerge(
                    'relative flex shrink-0 cursor-pointer items-center gap-1.5 rounded-full px-4 py-2.5 text-base font-normal no-underline [-webkit-tap-highlight-color:transparent]',
                    isActive ? 'bg-primary !text-white' : 'bg-slate-grey-100-alt text-black',
                )}
            >
                <FontAwesomeIcon icon={section.icon} className="text-base" />
                {label}
                {badge && <UnreadBadge className="absolute -end-1 -top-1 border border-white" />}
            </div>
        );
    }

    return (
        <NavLink
            to={section.path}
            className={({ isActive: active }) =>
                twMerge(
                    'relative flex shrink-0 items-center gap-1.5 rounded-full px-4 py-2.5 text-base font-normal no-underline [-webkit-tap-highlight-color:transparent]',
                    active
                        ? 'bg-primary !text-white hover:!text-white active:!text-white'
                        : 'bg-slate-grey-100-alt text-black',
                )
            }
        >
            <FontAwesomeIcon icon={section.icon} className="text-base" />
            {label}
            {badge && <UnreadBadge className="absolute -end-1 -top-1 border border-white" />}
        </NavLink>
    );
}

// --- Presentation components for Storybook ---

type SectionNavPresentationProps = {
    sections: NavSection[];
    labels: Record<string, string>;
    unreadCount: number;
};

function PresentationUnreadBadge({ count, className }: { count: number; className?: string }) {
    if (count <= 0) return null;
    const label = count > 99 ? '99+' : String(count);
    return (
        <span
            className={twMerge(
                'flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-blue px-1 text-[10px] font-semibold leading-none text-white',
                className,
            )}
        >
            {label}
        </span>
    );
}

export function DesktopSectionNavPresentation({ sections, labels, unreadCount }: SectionNavPresentationProps) {
    return (
        <Card padding={0} radius={8} className="w-[220px]">
            <div className="flex flex-col items-start gap-2.5 px-5 py-10">
                {sections.map(section => (
                    <NavLink
                        key={section.path}
                        to={section.path}
                        className={({ isActive }) =>
                            twMerge(
                                'relative flex w-fit items-center gap-3.5 rounded-full px-5 py-3.5 text-lg font-normal text-black no-underline transition-colors',
                                isActive ? 'bg-slate-grey-100-alt' : 'hover:bg-slate-grey-50',
                            )
                        }
                    >
                        <FontAwesomeIcon icon={section.icon} className="w-6 text-center text-lg" fixedWidth />
                        {labels[section.labelKey] ?? section.labelKey}
                        {section.labelKey === 'messages' && (
                            <PresentationUnreadBadge
                                count={unreadCount}
                                className="absolute -end-0.5 -top-0.5 h-[22px] min-w-[22px] border-2 border-white text-xs"
                            />
                        )}
                    </NavLink>
                ))}
            </div>
        </Card>
    );
}

export function MobileSectionNavPresentation({
    sections,
    labels,
    unreadCount,
    isMapView = false,
}: SectionNavPresentationProps & { isMapView?: boolean }) {
    return (
        <div className="relative">
            {isMapView && (
                <>
                    <div
                        className="pointer-events-none absolute inset-x-0"
                        style={{
                            bottom: '40%',
                            height: 80,
                            backgroundImage: `url(${mobileFadeSrc})`,
                            backgroundRepeat: 'repeat-x',
                            backgroundPosition: 'left bottom',
                            backgroundSize: 'auto 100%',
                            transform: 'scaleY(-1)',
                        }}
                    />
                    <div
                        className="pointer-events-none absolute inset-x-0"
                        style={{
                            top: '35%',
                            height: 80,
                            backgroundImage: `url(${mobileFadeSrc})`,
                            backgroundRepeat: 'repeat-x',
                            backgroundPosition: 'left top',
                            backgroundSize: 'auto 100%',
                        }}
                    />
                </>
            )}
            <div className={isMapView ? 'py-5' : 'bg-white py-2.5'}>
                <nav className="hide-scrollbar relative flex gap-1.5 overflow-x-auto px-2.5 pt-1.5">
                    {sections.map(section => (
                        <NavLink
                            key={section.path}
                            to={section.path}
                            className={({ isActive }) =>
                                twMerge(
                                    'relative flex shrink-0 items-center gap-1.5 rounded-full px-4 py-2.5 text-base font-normal no-underline [-webkit-tap-highlight-color:transparent]',
                                    isActive
                                        ? 'bg-primary !text-white hover:!text-white active:!text-white'
                                        : 'bg-slate-grey-100-alt text-black',
                                )
                            }
                        >
                            <FontAwesomeIcon icon={section.icon} className="text-base" />
                            {labels[section.labelKey] ?? section.labelKey}
                            {section.labelKey === 'messages' && (
                                <PresentationUnreadBadge
                                    count={unreadCount}
                                    className="absolute -end-1 -top-1 border border-white"
                                />
                            )}
                        </NavLink>
                    ))}
                </nav>
            </div>
        </div>
    );
}

// --- Smart (connected) components ---

export function DesktopSectionNav() {
    const { t } = useTranslation('back_royal', { keyPrefix: 'studentNetworkV2.studentNetworkV2.nav' });
    const sections = useSections();

    return (
        <Card padding={0} radius={8} className="w-[220px]">
            <div className="flex flex-col items-start gap-2.5 px-5 py-10">
                {sections.map(section => (
                    <DesktopNavItem
                        key={section.path}
                        section={section}
                        label={t(section.labelKey)}
                        badge={section.labelKey === 'messages'}
                    />
                ))}
            </div>
        </Card>
    );
}

export function MobileSectionNav({ isMapView = false }: { isMapView?: boolean } = {}) {
    const { t } = useTranslation('back_royal', { keyPrefix: 'studentNetworkV2.studentNetworkV2.nav' });
    const sections = useSections();
    const navRef = useRef<HTMLElement>(null);
    const location = useLocation();

    // Center the active item after route changes
    useEffect(() => {
        const nav = navRef.current;
        if (!nav) return;

        const activeItem = nav.querySelector<HTMLElement>('.bg-primary');
        if (!activeItem) return;

        const navRect = nav.getBoundingClientRect();
        const itemRect = activeItem.getBoundingClientRect();
        const scrollLeft = activeItem.offsetLeft - navRect.width / 2 + itemRect.width / 2;
        nav.scrollTo({ left: scrollLeft, behavior: 'smooth' });
    }, [location.pathname]);

    return (
        <div className="relative">
            {/* On map view: two fade strips mirrored from nav vertical center, tiled on x */}
            {isMapView && (
                <>
                    <div
                        className="pointer-events-none absolute inset-x-0"
                        style={{
                            bottom: '40%',
                            height: 80,
                            backgroundImage: `url(${mobileFadeSrc})`,
                            backgroundRepeat: 'repeat-x',
                            backgroundPosition: 'left bottom',
                            backgroundSize: 'auto 100%',
                            transform: 'scaleY(-1)',
                        }}
                    />
                    <div
                        className="pointer-events-none absolute inset-x-0"
                        style={{
                            top: '35%',
                            height: 80,
                            backgroundImage: `url(${mobileFadeSrc})`,
                            backgroundRepeat: 'repeat-x',
                            backgroundPosition: 'left top',
                            backgroundSize: 'auto 100%',
                        }}
                    />
                </>
            )}
            <div className={isMapView ? 'py-5' : 'bg-white py-2.5'}>
                <nav ref={navRef} className="hide-scrollbar relative flex gap-1.5 overflow-x-auto px-2.5 pt-1.5">
                    {sections.map(section => (
                        <MobileNavItem
                            key={section.path}
                            section={section}
                            label={t(section.labelKey)}
                            badge={section.labelKey === 'messages'}
                        />
                    ))}
                </nav>
            </div>
        </div>
    );
}
